import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Code, Field, Section, SourceRefs } from './DetailPrimitives';
import OntologyMeshSection from './OntologyMeshSection';
import { formatValue } from './ontologyFormat';
import { dsVar, ink } from '../../theme/tokens';

/**
 * A parameter: the value, where it came from, and the bound it is checked against.
 *
 * How it REACHES what it governs — the binding and the scope — reads on the blast-radius
 * rail beside this, in OntologyReachRail, next to the agents that binding lands on. A
 * parameter with no concept code is still called out here, on the value itself, rather
 * than displayed as if it were working: it resolves but governs nothing.
 */
const ParameterDetailBody: React.FC<{ document: Record<string, any> }> = ({ document }) => {
    const constraint = document.constraint || {};

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, color: dsVar('accent-fg'), lineHeight: 1.2 }}>
                    {formatValue(document.value, document.unit)}
                </Typography>
                {document.operator && (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                        applied as <Code>{document.operator}</Code>
                    </Typography>
                )}
            </Box>

            {!document.concept_code && (
                <Box
                    sx={{
                        display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: 2,
                        p: 1.5, borderRadius: '6px',
                        backgroundColor: 'rgba(248, 113, 113, 0.08)',
                        border: '1px solid rgba(248, 113, 113, 0.35)',
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 18, color: ink('#F87171'), mt: '1px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5'), lineHeight: 1.6 }}>
                        This parameter names no concept, so it resolves but governs nothing. It will
                        never be applied by any node.
                    </Typography>
                </Box>
            )}

            <Section title="Provenance">
                <Field label="Source" value={document.source_type && <Code>{document.source_type}</Code>} />
                <Field label="Confidence" value={document.confidence && String(document.confidence)} />
                <Field label="Inference method" value={document.inference_method} />
                {/* Must stay a string — the engine writes it into Neo4j as a node property,
                    and a map there fails the entire graph projection. */}
                <Field label="Evidence" value={document.evidence_ref && <Code>{document.evidence_ref}</Code>} />
                <Field label="Note" value={document.note} />
            </Section>

            {document.proposal_note && (
                <Section title="Proposal note">
                    <Box
                        sx={{
                            p: 1.5, borderRadius: '6px',
                            backgroundColor: 'rgba(251, 191, 36, 0.06)',
                            border: '1px solid rgba(251, 191, 36, 0.3)',
                        }}
                    >
                        <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCD34D'), lineHeight: 1.6 }}>
                            {document.proposal_note}
                        </Typography>
                    </Box>
                </Section>
            )}

            {/* Only meaningful on a regulatory document: the bound an org value is checked
                against. Which direction "stricter" runs depends on the threshold, so the
                regulatory document states the comparator rather than the checker guessing. */}
            {constraint.operator && (
                <Section title="Constraint on organisation values">
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>
                        An organisation value must be{' '}
                        <Code>{constraint.operator} {String(constraint.value)}</Code>
                        {document.unit ? ` ${document.unit}` : ''}.
                    </Typography>
                </Section>
            )}

            {/* Directly after provenance: "where did this come from" and "what does it
                reach" are the two halves of the same question about a threshold. */}
            <OntologyMeshSection kind="parameters" code={document.parameter_code} />

            <Section title="Sources">
                <SourceRefs refs={document.source_refs} />
            </Section>
        </Box>
    );
};

export default ParameterDetailBody;
